import React, { PropsWithChildren, useEffect, useState } from 'react';
import cn from 'classnames';
import Avatar from '@views/Contact/ProfileEditor/Avatar';
import Panel from '../Panel';
import CopySection, { FinishStepButton } from '../CopySection';
import styles from './ProfilePanel.module.scss';
import { UserSession } from '@repo/utils/types';
import analytics from '@repo/utils/helpers/analytics';
import { useImageLoadingCallback, useAuthContext } from '@repo/hooks';
import validEmail from '@repo/utils/helpers/valid-email';
import {
  checkStorageForSignInWithApplePII,
  clearSignInWithApplePII,
} from '@repo/utils/helpers/jwt';

type UserSessionField = keyof UserSession;
type InvalidFields = { [K in UserSessionField]?: boolean };

function findInvalidFields(user: Partial<UserSession>) {
  const invalidFields: InvalidFields = {};

  if (!user.first_name) {
    invalidFields.first_name = true;
  }

  if (!user.last_name) {
    invalidFields.last_name = true;
  }

  if (!validEmail(user.email)) {
    invalidFields.email = true;
  }

  // do we want to check for well formed linkedin/twitter URLs?

  return Object.keys(invalidFields).length > 0 ? invalidFields : null;
}

const FormField = ({
  label,
  children,
  optional,
  additionalInfo,
}: PropsWithChildren<{ label: string; optional?: boolean; additionalInfo?: string }>) => (
  <div>
    <label className="form-label">
      {label} {optional && <span className="optional">(optional)</span>}{' '}
      {additionalInfo && <span className={styles.additionalInfo}>{additionalInfo}</span>}
    </label>
    {children}
  </div>
);

export default function ProfilePanel({ proceedToNextStep }) {
  const { user, updateUserProfile } = useAuthContext();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingFailed, setSavingFailed] = useState(false);
  const [userProfile, setUserProfile] = useState({ ...user });
  const [avatarImgToUpload, setAvatarImgToUpload] = useState<null | File>(null);
  const [invalidFormFields, setInvalidFormFields] = useState(null);
  const [dirtyFormFields, setDirtyFormFields] = useState({});
  const [avatarOptionsLoaded, setAvatarOptionsLoaded] = useState(false);

  useImageLoadingCallback(userProfile.avatar_options || [], () => setAvatarOptionsLoaded(true));

  const updateProfileField = (field: UserSessionField) => (e) => {
    if (!dirtyFormFields[field]) {
      setDirtyFormFields({ ...dirtyFormFields, [field]: true });
    }
    setUserProfile({ ...userProfile, [field]: e.target.value });
  };

  const setAvatar = (avatar_url, fileToUpload) => {
    setUserProfile({ ...userProfile, avatar_url });
    setAvatarImgToUpload(fileToUpload);
  };

  const approveProfileAndGoToNextStep = async () => {
    setSavingProfile(true);
    const updatedProfile = {
      ...userProfile,
      avatar_url: avatarImgToUpload || userProfile.avatar_url,
    };

    const userProfileUpdated = await updateUserProfile(updatedProfile);
    if (!userProfileUpdated) {
      setSavingProfile(false);
      setSavingFailed(true);
    } else {
      analytics.trackProfileCreated();
      clearSignInWithApplePII();
      proceedToNextStep();
    }
  };

  const markIfInvalid = (field: UserSessionField) =>
    dirtyFormFields[field] && invalidFormFields?.[field] && 'invalid';

  useEffect(() => {
    const siwaPII = checkStorageForSignInWithApplePII();
    if (siwaPII && (siwaPII?.firstName || siwaPII?.lastName || siwaPII?.email)) {
      setUserProfile({
        ...userProfile,
        first_name: userProfile.first_name || siwaPII.firstName,
        last_name: userProfile.last_name || siwaPII.lastName,
        email: userProfile.email || siwaPII.email,
      });
    }
  }, []);

  useEffect(() => {
    analytics.track('Onboarding in Progress', { screen: 'Profile' });
  }, []);

  useEffect(() => {
    setInvalidFormFields(findInvalidFields(userProfile));

    if (savingFailed) {
      setSavingFailed(false);
    }
  }, [userProfile]);

  return (
    <Panel>
      <CopySection>
        <h2>Your Clay Profile</h2>
        <p>Personalize how you'll appear to people you know within Clay.</p>
        <p>We've given you a head start with a few profile suggestions.</p>
        <FinishStepButton
          onClick={approveProfileAndGoToNextStep}
          disabled={!!invalidFormFields || savingProfile}
          disabledMsg="Complete your profile to continue"
          loading={savingProfile}
          errorMsg={savingFailed && 'Something went wrong, please try again'}
        >
          Continue
        </FinishStepButton>
      </CopySection>
      <div className={styles.profileForm}>
        <Avatar
          className={cn(styles.avatarEditor, avatarOptionsLoaded && styles.loaded)}
          value={userProfile.avatar_url || null}
          avatar={{
            key: 'avatar_url',
            type: 'gallery',
            options: userProfile.avatar_options || [],
            value: userProfile.avatar_url || null,
          }}
          onChange={setAvatar}
          displayName={`${userProfile.first_name} ${userProfile.last_name}`}
          initials={userProfile.first_name[0] || ''}
        />
        <FormField label="First Name">
          <input
            className={cn('form-input', markIfInvalid('first_name'))}
            type="text"
            value={userProfile.first_name}
            onChange={updateProfileField('first_name')}
          />
        </FormField>
        <FormField label="Last Name">
          <input
            className={cn('form-input', markIfInvalid('last_name'))}
            type="text"
            value={userProfile.last_name}
            onChange={updateProfileField('last_name')}
          />
        </FormField>
        <FormField
          label="Email"
          additionalInfo={
            userProfile.email.includes('privaterelay.appleid.com')
              ? 'In order for our support team to provide the best experience, we recommend updating your email address.'
              : undefined
          }
        >
          <input
            className={cn('form-input', markIfInvalid('email'))}
            type="email"
            value={userProfile.email}
            onChange={updateProfileField('email')}
          />
        </FormField>
        <FormField label="Twitter" optional>
          <input
            className="form-input"
            type="url"
            value={userProfile.social_twitter_url}
            onChange={updateProfileField('social_twitter_url')}
            placeholder="@your_username"
          />
        </FormField>
        <FormField label="LinkedIn" optional>
          <input
            className="form-input"
            type="url"
            value={userProfile.social_linkedin_url}
            onChange={updateProfileField('social_linkedin_url')}
            placeholder="https://www.linkedin.com/in/yourUsername/"
          />
        </FormField>
      </div>
    </Panel>
  );
}
